import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useEffect } from 'react'

import { getCurrentUser } from '@/api/actions/auth/authActions'
import { Queries } from '@/api/queries.enum'
import type { RootState } from '@/store'
import type { UserData } from '@/types/auth.types'
import { hasValidTokens, getAuthToken } from '@/utils/auth/tokenManager'
import { logger } from '@/utils/logger'

export function useUserMetadata() {
    const { isAuthenticated, isMockAuth, user } = useSelector((state: RootState) => state.auth)

    // Check if we have tokens in cookies
    const hasTokens = hasValidTokens()

    // Enable query if we have tokens OR if already authenticated
    // This allows the hook to fetch user data on initial load when tokens exist
    const shouldFetchUser = (hasTokens || isAuthenticated) && !isMockAuth

    // Log the current state for debugging
    logger.debug('[UserMetadata] Hook state', {
        isAuthenticated,
        isMockAuth,
        hasUser: !!user,
        hasTokens,
        shouldFetchUser,
    })

    // Always call useQuery to maintain hooks order
    const queryResult = useQuery<UserData>({
        queryKey: [Queries.getUserMetadata],
        queryFn: () => {
            logger.info('[UserMetadata] API call initiated', {
                hasAccessToken: !!getAuthToken('access'),
                hasRefreshToken: !!getAuthToken('refresh'),
                timestamp: new Date().toISOString(),
            })
            return getCurrentUser()
        },
        enabled: shouldFetchUser,
        retry: (failureCount, error) => {
            logger.warn('[UserMetadata] API call failed, retry attempt', {
                failureCount,
                maxRetries: 2,
                error: error?.message,
                willRetry: failureCount < 2,
            })
            return failureCount < 2
        },
        // Allow refetch on window focus and when explicitly called
        refetchOnWindowFocus: false, // Disable to prevent excessive calls
        refetchOnMount: true,
        // Keep data fresh but not too aggressive
        staleTime: 30 * 1000, // 30 seconds before data is considered stale
        gcTime: 5 * 60 * 1000, // 5 minutes garbage collection time
    })

    // Log success/error using useEffect instead of deprecated onSuccess/onError
    useEffect(() => {
        if (queryResult.isSuccess && queryResult.data) {
            logger.info('[UserMetadata] API call successful', {
                userId: queryResult.data.uuid,
                userEmail: queryResult.data.email,
                organizationId: queryResult.data.organization?.id,
            })
        }
    }, [queryResult.isSuccess, queryResult.data])

    useEffect(() => {
        if (queryResult.isError && queryResult.error) {
            const axiosError = queryResult.error as { response?: { status?: number; statusText?: string } }
            logger.error('[UserMetadata] API call failed', {
                error: queryResult.error.message,
                status: axiosError.response?.status,
                statusText: axiosError.response?.statusText,
                hasAccessToken: !!getAuthToken('access'),
                hasRefreshToken: !!getAuthToken('refresh'),
            })
        }
    }, [queryResult.isError, queryResult.error])

    // For mock auth, override the query result with Redux state data
    if (isMockAuth && user) {
        return {
            ...queryResult,
            data: user,
            isLoading: false,
            error: null,
            isError: false,
            isSuccess: true,
            status: 'success' as const,
            fetchStatus: 'idle' as const,
        }
    }

    return queryResult
}
